import React, { useEffect, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { CloseIcon, CloudUpload } from 'src/assets/images';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import { filterObjectByKeys } from 'src/helpers/functional-helpers';

export enum AcceptedFileTypes {
  pdf = 'application/pdf',
  jpeg = 'image/jpeg',
  png = 'image/png',
}

const MimeTypeExtensionMap = {
  [AcceptedFileTypes.pdf]: ['pdf'],
  [AcceptedFileTypes.jpeg]: ['jpeg', 'jpg'],
  [AcceptedFileTypes.png]: ['png'],
};

type RevampFileUploadProps = {
  name: string;
  title: string;
  subtext: string;
  maxFileSizeText: string;
  acceptedFileTypes: AcceptedFileTypes[];
  maxFiles?: number;
  maxSize?: number;
  multiple?: boolean;
  stripPrefix?: boolean; // Strip the base64 prefix
  setFileTypes?: React.Dispatch<React.SetStateAction<string[]>>;
  onUpload?: () => void;
};

const RevampFileUpload = ({
  name,
  title,
  subtext,
  maxFileSizeText,
  acceptedFileTypes,
  maxFiles = 0,
  maxSize = 512000,
  multiple = true,
  stripPrefix = false,
  setFileTypes,
  onUpload,
}: RevampFileUploadProps) => {
  const [documents, setDocuments] = useState<File[]>([]);
  const [disableDrop, setDisableDrop] = useState<boolean>(false);

  const { isDragActive, getRootProps, getInputProps, open } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: filterObjectByKeys(MimeTypeExtensionMap, acceptedFileTypes),
    minSize: 0,
    maxFiles: maxFiles,
    maxSize: maxSize,
    multiple: multiple,
    disabled: disableDrop,
    validator: fileExtensionValidator,
  });
  const [field] = useField(name);
  const form = useFormikContext();

  const fileTypes = (acceptedFileTypes: AcceptedFileTypes[]) => {
    return acceptedFileTypes
      .map((fileType) => fileType.substring(fileType.indexOf('/') + 1).toUpperCase())
      .join('/');
  };

  const toBase64 = (file: File): Promise<string | null> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function onDropAccepted(files: File[], _event: DropEvent) {
    if ([...documents, ...files].length > maxFiles) {
      const message = `Too many files, please choose ${maxFiles - documents.length}`;
      toastMessageServiceSingleton.addError(message, null, 'N/A', false, null, true);
      return;
    }
    setDocuments([...documents, ...files]);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function onDropRejected(fileRejections: FileRejection[], _event: DropEvent) {
    switch (fileRejections[0].errors[0].code) {
      case 'file-invalid-type': {
        toastMessageServiceSingleton.addError(
          `File upload failed: Incorrect file type, only ${fileTypes(
            acceptedFileTypes,
          )} supported.`,
          null,
          'N/A',
          true,
          null,
          true,
        );
        break;
      }

      case 'file-too-large': {
        const errorMessage = `File upload failed: Maximum file size of ${
          maxSize > 1000000 ? `${maxSize / 1024000}MB` : `${maxSize / 1024}KB`
        } exceeded.`;
        toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', true, null, true);
        break;
      }

      default: {
        const errorMessage = fileRejections[0].errors[0].message;
        toastMessageServiceSingleton.addError(errorMessage, null, 'N/A', false, null, true);
      }
    }
  }

  function removeDoc(index: number) {
    const newDocs = [...documents];
    newDocs.splice(index, 1);
    setDocuments(newDocs);
  }

  function fileExtensionValidator(file: File) {
    const ext = file.name.substring(file.name.lastIndexOf('.') + 1);

    const filteredExtensions = acceptedFileTypes.map((mime) => MimeTypeExtensionMap[mime]).flat();
    if (filteredExtensions.some((item) => item == ext)) {
      return null;
    }
    return {
      code: 'file-invalid-type',
      message: `Invalid file extension(${ext}).`,
    };
  }

  useEffect(() => {
    if (documents && documents.length > maxFiles) {
      return;
    }

    setDisableDrop(documents && documents.length >= maxFiles);

    if (setFileTypes) {
      setFileTypes(documents.map((file: File) => file.type));
    }

    if (documents.length && onUpload) {
      onUpload();
    }

    const base64Docs = documents.map(async (doc) => {
      try {
        let result = await toBase64(doc);
        if (result && stripPrefix) {
          result = result.replace(/^data:[^;]+;base64,/, '');
        }
        return result;
      } catch (error) {
        console.error(error);
        return;
      }
    });
    Promise.all(base64Docs).then((values) => {
      form.setFieldValue(field.name, values);
    });
  }, [documents]);

  return (
    <section className="flex flex-col w-full p-2 gap-y-2">
      <h6 className="text-base text-grey-200">{title}</h6>

      <div
        className={`flex flex-col gap-3 w-full max-w-[500px] border-[2px] ${
          isDragActive ? 'border-solid border-yellow-800' : 'border-dashed border-grey-600'
        } rounded-16 p-4`}
      >
        <div
          {...getRootProps({ className: 'dropzone' })}
          className={`flex flex-col gap-2 ${disableDrop ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={(event) => {
            event.stopPropagation();
            if (!disableDrop) {
              open();
            }
          }}
        >
          <input
            type="file"
            id={`revamp-file-upload-${name}`}
            name={name}
            multiple={multiple}
            {...getInputProps()}
          />
          {documents.length > 0 && (
            <div className="flex flex-wrap w-full gap-2">
              {documents.map((file, index) => (
                <div
                  className="flex items-center px-3 py-2 bg-grey-100 rounded-40 w-full min-w-[85px] max-w-[200px]"
                  key={index}
                  onClick={(event) => event.stopPropagation()}
                >
                  <p className="w-full overflow-hidden text-xs text-grey-800 text-ellipsis whitespace-nowrap">
                    {file.name}
                  </p>
                  <img
                    className="w-6 h-6 cursor-pointer"
                    src={CloseIcon}
                    alt="close"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeDoc(index);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <img src={CloudUpload} alt="upload" className="w-6 h-6" />
            <p className="text-grey-400">
              Drag & drop file here or click to{' '}
              <span className="text-base text-white-50">Upload</span>
            </p>
          </div>
        </div>
      </div>

      <aside className="flex justify-between">
        <p className="text-grey-400 max-w-[19rem] w-full">{subtext}</p>
        <p className="text-right text-grey-400">{maxFileSizeText}</p>
      </aside>
    </section>
  );
};

export default RevampFileUpload;
